/**
 * Funnelback-powered search field with autocomplete (shared by header drawer and page search).
 */
import {
  useState,
  ChangeEvent,
  FormEvent,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import debounce from 'lodash/debounce';
import Suggestions from './suggestions';
import type { FunnelbackSuggestion } from './suggestion';
import SearchIcon from './search-icon';
import ClearButton from './clear-button';
import { captureNetworkError } from '../../sentry/capture-network-error';
import { parseFunnelbackJsonResponse } from '../../utils/parse-funnelback-json-response';
import { assertSafeFunnelbackFetchUrl } from '../../utils/funnelback-fetch-url';
import { fetchWithFunnelbackRetry } from '../../utils/funnelback-fetch';
import { resolveFunnelbackUrlsFromMeta } from '../../funnelback/funnelback-base-url';

export type { FunnelbackSuggestion } from './suggestion';
export { buildLookupSearchUrl } from './build-lookup-search-url';

export type FunnelbackSearchFormProps = {
  /** Block prefix, e.g. `header-search` → `header-search-form`, `header-search-suggestions`. */
  baseClassName: string;
  /** Base suggest.json URL; validates autocomplete fetch targets before requesting. */
  autocompleteUrlBase?: string;
  getAutocompleteUrl: (partialQuery: string) => string;
  buildSearchUrl: (query: string) => string;
  placeholder: string;
  /**
   * Controlled query (`search-app` context). When set, use with `onQueryChange`.
   */
  controlledQuery?: string;
  onQueryChange?: (query: string) => void;
  /** When true, submit and suggestion pick call this instead of full-page navigation. */
  onSearchCommit?: (query: string) => void;
  /** Allow submitting with an empty query (browse-all on landing). */
  allowEmptySubmit?: boolean;
  /** When true, focus input after mount (e.g. header drawer open). */
  autoFocus?: boolean;
  /** Invoked when Escape is pressed with an empty query and no open suggestion list. */
  onEscapeWhenEmpty?: () => void;
  /**
   * When suggestions are open, focusing an element matching this selector hides the list
   * (e.g. header drawer close control).
   */
  hideSuggestionsOnFocusWithinSelector?: string;
  formAction?: string | (() => void);
  labelText?: string;
};

const FunnelbackSearchForm = (props: FunnelbackSearchFormProps) => {
  const {
    baseClassName,
    autocompleteUrlBase,
    getAutocompleteUrl,
    buildSearchUrl,
    placeholder,
    controlledQuery,
    onQueryChange,
    onSearchCommit,
    allowEmptySubmit = false,
    autoFocus = false,
    onEscapeWhenEmpty,
    hideSuggestionsOnFocusWithinSelector,
    formAction = '/search',
    labelText = 'Search',
  } = props;

  const [internalTerm, setInternalTerm] = useState('');
  const inputValue =
    controlledQuery !== undefined ? controlledQuery : internalTerm;
  const [suggestions, setSuggestions] = useState<FunnelbackSuggestion[]>([]);
  const [suggestionsVisible, setSuggestionsVisible] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputRowRef = useRef<HTMLDivElement>(null);
  const suggestionsListRef = useRef<HTMLUListElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const blurHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const suggestBaseUrl = useMemo(() => {
    const fromProp = autocompleteUrlBase?.trim() ?? '';
    if (fromProp) {
      return fromProp;
    }
    return resolveFunnelbackUrlsFromMeta().suggestBaseUrl;
  }, [autocompleteUrlBase]);

  const suggestionTriggerThreshold = 2;
  /** Aligned with search-app query debounce in `useSearchAppQuery`. */
  const suggestionLookupDelay = 350;

  useEffect(() => {
    if (!autoFocus || !inputRef.current) {
      return;
    }
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(() => {
          inputRef.current?.focus();
        }, 10);
      });
    });
  }, [autoFocus]);

  const cancelSuggestionsRequest = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  /** After clearing suggestions, defer focus so `handleFocus` sees committed state (not a stale non-empty list). */
  const focusInputAfterFlush = useCallback(() => {
    queueMicrotask(() => {
      inputRef.current?.focus();
    });
  }, []);

  const getSearchSuggestions = useCallback(
    (partialQuery: string) => {
      const candidate = getAutocompleteUrl(partialQuery);
      if (!candidate) {
        return;
      }
      const url = suggestBaseUrl
        ? assertSafeFunnelbackFetchUrl(candidate, suggestBaseUrl)
        : candidate;
      if (!url) {
        return;
      }

      cancelSuggestionsRequest();

      abortControllerRef.current = new AbortController();

      fetchWithFunnelbackRetry(url, {
        signal: abortControllerRef.current.signal,
      })
        .then((response) =>
          parseFunnelbackJsonResponse<FunnelbackSuggestion[]>(response),
        )
        .then((data) => {
          abortControllerRef.current = null;
          const suggestions = Array.isArray(data) ? data : [];
          setSuggestions(suggestions);
          setSuggestionsVisible(suggestions.length > 0);
          setFocusedIndex(null);
        })
        .catch((error) => {
          if (error.name !== 'AbortError') {
            console.error('Error fetching autocomplete data:', error);
            captureNetworkError(error, {
              component: 'FunnelbackSearchForm',
              request: 'autocomplete',
              url,
            });
            setSuggestions([]);
            setSuggestionsVisible(false);
          }
        });
    },
    [suggestBaseUrl, getAutocompleteUrl, cancelSuggestionsRequest],
  );

  const debouncedGetSuggestions = useMemo(
    () =>
      debounce((query: string) => {
        if (query.length >= suggestionTriggerThreshold) {
          getSearchSuggestions(query);
        } else {
          cancelSuggestionsRequest();
          setSuggestions([]);
          setSuggestionsVisible(false);
        }
      }, suggestionLookupDelay),
    [getSearchSuggestions, cancelSuggestionsRequest],
  );

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (controlledQuery !== undefined) {
      onQueryChange?.(value);
    } else {
      setInternalTerm(value);
    }
    debouncedGetSuggestions(value);
  };

  const handleFocus = () => {
    if (suggestions.length > 0) {
      setSuggestionsVisible(true);
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    debouncedGetSuggestions.cancel();
    cancelSuggestionsRequest();
    setSuggestions([]);
    setSuggestionsVisible(false);
    setFocusedIndex(null);
    const trimmed = inputValue.trim();
    if (onSearchCommit) {
      // Search app: update query only; `useSearchAppQuery` performs the Funnelback `search.json` fetch.
      onSearchCommit(trimmed);
      return;
    }
    // Header (and other embeds without `onSearchCommit`): navigate to the site search page.
    window.location.href = buildSearchUrl(trimmed);
  };

  const applyEscapeKeyBehavior = useCallback(
    (
      e: Pick<
        React.KeyboardEvent<Element>,
        'preventDefault' | 'stopPropagation'
      >,
    ) => {
      e.stopPropagation();
      debouncedGetSuggestions.cancel();
      cancelSuggestionsRequest();
      if (suggestionsVisible) {
        e.preventDefault();
        setSuggestions([]);
        setSuggestionsVisible(false);
        setFocusedIndex(null);
        focusInputAfterFlush();
      } else if (inputValue.length > 0) {
        e.preventDefault();
        if (controlledQuery !== undefined) {
          onQueryChange?.('');
        } else {
          setInternalTerm('');
        }
        setSuggestions([]);
        setSuggestionsVisible(false);
      } else {
        e.preventDefault();
        onEscapeWhenEmpty?.();
      }
    },
    [
      suggestionsVisible,
      inputValue,
      onEscapeWhenEmpty,
      controlledQuery,
      onQueryChange,
      debouncedGetSuggestions,
      cancelSuggestionsRequest,
      focusInputAfterFlush,
    ],
  );

  const handleInputRowKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Escape') {
      return;
    }
    const t = e.target as Node;
    const inputEl = inputRef.current;
    if (inputEl && (t === inputEl || inputEl.contains(t))) {
      return;
    }
    if (!inputRowRef.current?.contains(t)) {
      return;
    }
    if (suggestionsVisible && inputValue.length > 0) {
      e.preventDefault();
      e.stopPropagation();
      setFocusedIndex(null);
      inputRef.current?.focus();
      return;
    }
    applyEscapeKeyBehavior(e);
  };

  const handleBlur = () => {
    if (blurHideTimeoutRef.current !== null) {
      clearTimeout(blurHideTimeoutRef.current);
    }
    blurHideTimeoutRef.current = setTimeout(() => {
      blurHideTimeoutRef.current = null;
      if (typeof document === 'undefined') {
        return;
      }
      const activeElement = document.activeElement;
      const suggestionsRoot = document.getElementById(
        `${baseClassName}-suggestions`,
      );

      if (activeElement && suggestionsRoot?.contains(activeElement)) {
        return;
      }

      if (activeElement && inputRowRef.current?.contains(activeElement)) {
        return;
      }

      setSuggestionsVisible(false);
      setFocusedIndex(null);
    }, 250);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      applyEscapeKeyBehavior(e);
      return;
    }

    if (!suggestionsVisible || suggestions.length === 0) {
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex((prev) => {
          if (prev === null) return 0;
          return prev < suggestions.length - 1 ? prev + 1 : 0;
        });
        setSuggestionsVisible(true);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex((prev) => {
          if (prev === null) return suggestions.length - 1;
          return prev > 0 ? prev - 1 : suggestions.length - 1;
        });
        setSuggestionsVisible(true);
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex !== null && suggestions[focusedIndex]) {
          const selectedSuggestion = suggestions[focusedIndex];
          const selectedTerm = selectedSuggestion.disp;
          if (controlledQuery !== undefined) {
            onQueryChange?.(selectedTerm);
          } else {
            setInternalTerm(selectedTerm);
          }
          setSuggestions([]);
          setSuggestionsVisible(false);
          setFocusedIndex(null);
          if (onSearchCommit) {
            onSearchCommit(selectedTerm.trim());
          } else {
            window.location.href = buildSearchUrl(selectedTerm);
          }
        } else {
          handleSubmit(e as unknown as FormEvent<HTMLFormElement>);
        }
        break;
    }
  };

  const handleSuggestionKeyDown = (
    e: React.KeyboardEvent<HTMLLIElement>,
    index: number,
  ) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex((prev) => {
          if (prev === null) return 0;
          return prev < suggestions.length - 1 ? prev + 1 : 0;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex((prev) => {
          if (prev === null) return suggestions.length - 1;
          if (prev === 0) {
            inputRef.current?.focus();
            setFocusedIndex(null);
            return null;
          }
          return prev - 1;
        });
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (suggestions[index]) {
          handleSuggestionClick(suggestions[index]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        e.stopPropagation();
        setSuggestions([]);
        setSuggestionsVisible(false);
        setFocusedIndex(null);
        focusInputAfterFlush();
        break;
    }
  };

  const handleSuggestionFocus = (index: number) => {
    setFocusedIndex(index);
  };

  const handleSuggestionClick = (suggestion: FunnelbackSuggestion) => {
    const selectedTerm = suggestion.disp;
    if (controlledQuery !== undefined) {
      onQueryChange?.(selectedTerm);
    } else {
      setInternalTerm(selectedTerm);
    }
    setSuggestions([]);
    setSuggestionsVisible(false);
    setFocusedIndex(null);
    if (onSearchCommit) {
      onSearchCommit(selectedTerm.trim());
    } else {
      window.location.href = buildSearchUrl(selectedTerm);
    }
  };

  const clearSearch = useCallback(() => {
    if (inputValue.length > 0) {
      if (controlledQuery !== undefined) {
        onQueryChange?.('');
      } else {
        setInternalTerm('');
      }
      setSuggestions([]);
      setSuggestionsVisible(false);
      setFocusedIndex(null);
      inputRef.current?.focus();
    }
  }, [controlledQuery, inputValue, onQueryChange]);

  useEffect(() => {
    return () => {
      cancelSuggestionsRequest();
      debouncedGetSuggestions.cancel();
      if (blurHideTimeoutRef.current !== null) {
        clearTimeout(blurHideTimeoutRef.current);
        blurHideTimeoutRef.current = null;
      }
    };
  }, [cancelSuggestionsRequest, debouncedGetSuggestions]);

  useEffect(() => {
    if (focusedIndex !== null && suggestionsListRef.current) {
      const suggestionElement = suggestionsListRef.current.querySelector(
        `#suggestion-${focusedIndex}`,
      ) as HTMLElement;
      if (suggestionElement && document.activeElement !== suggestionElement) {
        requestAnimationFrame(() => {
          suggestionElement.focus();
        });
      }
    }
  }, [focusedIndex]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const form = inputRef.current?.closest('form');
      if (form && !form.contains(target) && suggestionsVisible) {
        setSuggestionsVisible(false);
        setFocusedIndex(null);
      }
    };

    if (suggestionsVisible) {
      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [suggestionsVisible]);

  useEffect(() => {
    if (!suggestionsVisible || !hideSuggestionsOnFocusWithinSelector) {
      return;
    }

    const onFocusIn = (e: FocusEvent) => {
      const el = e.target as HTMLElement | null;
      if (!el) {
        return;
      }
      if (el.closest(hideSuggestionsOnFocusWithinSelector)) {
        setSuggestionsVisible(false);
        setFocusedIndex(null);
      }
    };

    document.addEventListener('focusin', onFocusIn, true);
    return () => document.removeEventListener('focusin', onFocusIn, true);
  }, [suggestionsVisible, hideSuggestionsOnFocusWithinSelector]);

  return (
    <form
      className={`${baseClassName}`}
      action={formAction}
      onSubmit={handleSubmit}
    >
      <label
        htmlFor={`${baseClassName}-input`}
        className={`${baseClassName}__label`}
      >
        {labelText}
      </label>
      <div
        ref={inputRowRef}
        className={`${baseClassName}__input-row${
          inputValue.length > 0 ? ` ${baseClassName}__input-row--has-value` : ''
        }`}
        onKeyDown={handleInputRowKeyDown}
      >
        <SearchIcon baseClassName={baseClassName} />
        <input
          ref={inputRef}
          id={`${baseClassName}-input`}
          type="search"
          role="combobox"
          aria-haspopup="listbox"
          placeholder={placeholder}
          className={`${baseClassName}__input`}
          value={inputValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          aria-expanded={suggestionsVisible}
          aria-autocomplete="list"
          aria-controls={`${baseClassName}-suggestions`}
          aria-activedescendant={
            focusedIndex !== null ? `suggestion-${focusedIndex}` : undefined
          }
        />
        <ClearButton
          baseClassName={baseClassName}
          searchTerm={inputValue}
          onClear={clearSearch}
        />
      </div>
      {suggestionsVisible ? (
        <Suggestions
          suggestions={suggestions}
          focusedIndex={focusedIndex}
          onSuggestionClick={handleSuggestionClick}
          onSuggestionKeyDown={handleSuggestionKeyDown}
          onSuggestionFocus={handleSuggestionFocus}
          listRef={suggestionsListRef}
          baseClassName={baseClassName}
        />
      ) : null}
    </form>
  );
};

export default FunnelbackSearchForm;
